import CaretRightOutlined from '@ant-design/icons/CaretRightOutlined';
import Collapse from 'antd/lib/collapse';
import List from 'antd/lib/list';
import Typography from 'antd/lib/typography';
import Spin from 'antd/lib/spin';
import Tooltip from 'antd/lib/tooltip';
import { format } from 'date-fns';

import { IAffectedSites } from '../../eventInterface';
import { getTimezoneFormattedTime } from '../../../../domain/date/date';
import './ResourceCollapsedList.css';

function getISOFormatWithoutTimezone(date: Date) {
    return format(date, 'yyyy-MM-dd HH:mm:ss');
}

function countUniqTimezones(resources: IAffectedSites[], datetime: Date): Map<string, number> {
    const counter = new Map<string, number>();

    resources.forEach(resource => {
        const shortFormat = getTimezoneFormattedTime(getISOFormatWithoutTimezone(datetime), resource.site_timezone, 'zzz');

        const hasTimezone = counter.has(shortFormat);

        if (!hasTimezone) {
            counter.set(shortFormat, 1);
        } else {
            const existedNumber = counter.get(shortFormat);

            counter.set(shortFormat, existedNumber! + 1);
        }
    });

    return counter;
}

export const ResourceCollapsedList = ({
    resources,
    eventDatetime = new Date(),
    loading = false,
    showTimezone = false,
}: {
    resources: IAffectedSites[];
    eventDatetime?: Date;
    loading?: boolean;
    showTimezone?: boolean;
}) => {
    const getGeneralTimezoneInfo = () => {
        const uniqTimezoneCounter = countUniqTimezones(resources, eventDatetime);

        return [...uniqTimezoneCounter.keys()]
            .map(key => {
                return `${key}: ${uniqTimezoneCounter.get(key)}`;
            })
            .join(', ');
    };

    return (
        <Collapse accordion size="small" expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}>
            <Collapse.Panel
                id={'resources-collapse-panel'}
                key="resources"
                header={
                    <Spin spinning={loading}>
                        <Typography.Text>
                            Affected {resources.length} site{resources.length > 1 && 's'}
                        </Typography.Text>
                    </Spin>
                }
                extra={showTimezone && <Typography.Text>{getGeneralTimezoneInfo()}</Typography.Text>}
                collapsible={loading ? 'disabled' : 'header'}
            >
                <List
                    style={{
                        maxHeight: '400px',
                        overflow: 'auto',
                    }}
                    size="small"
                    bordered={false}
                    dataSource={resources}
                    renderItem={item => {
                        const timezone = getTimezoneFormattedTime(getISOFormatWithoutTimezone(eventDatetime), item.site_timezone, 'zzz');

                        return (
                            <List.Item key={`resource_${item.id}`}>
                                <Tooltip title={`${item.company}/${item.site}`}>
                                    <Typography.Text>{`${item.company}/${item.site}`}</Typography.Text>
                                </Tooltip>
                                {showTimezone && (
                                    <Tooltip title={timezone}>
                                        <Typography.Text style={{ minWidth: '50px', display: 'flex', justifyContent: 'flex-end' }}>
                                            {timezone}
                                        </Typography.Text>
                                    </Tooltip>
                                )}
                            </List.Item>
                        );
                    }}
                />
            </Collapse.Panel>
        </Collapse>
    );
};
